import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faUpRightFromSquare,
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import { User } from '../types';
import UnreadBadge from './UnreadBadge';
import { roleBadgeClass } from '../utils/roleBadge';
import { DEFAULT_LOGGING_MODE, loggingModeShortLabel, loggingModeBadgeStyle } from '../utils/loggingMode';
import SentryDropdown, { sentryExactQuery } from './SentryDropdown';

interface TesterUsersTablePagination {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

interface TesterUsersTableProps {
  title: string;
  users: User[];
  usersTotal: number;
  loading: boolean;
  unreadByUserId: Record<string, number>;
  emptyMessage: string;
  onOpenChat: (user: User) => void;
  userProfileFrom?: 'internal-testers' | 'beta-testers';
  pagination?: TesterUsersTablePagination;
  toolbar?: React.ReactNode;
}

type BetaEnrollmentStatus = NonNullable<NonNullable<User['betaAppEnrollment']>['status']>;

const enrollmentStatusLabel = (status?: BetaEnrollmentStatus | null) => {
  switch (status) {
    case 'invited':
      return 'Invited';
    case 'already_invited':
      return 'Already invited';
    case 'accepted':
      return 'Accepted';
    case 'installed':
      return 'Installed';
    case 'instructions':
      return 'Instructions';
    case 'pending':
      return 'Pending';
    case 'error':
      return 'Needs follow-up';
    default:
      return '—';
  }
};

const enrollmentStatusBadgeClass = (status?: string | null) => {
  switch (status) {
    case 'installed':
      return 'bg-success';
    case 'invited':
    case 'already_invited':
    case 'accepted':
    case 'instructions':
      return 'bg-warning text-dark';
    case 'pending':
      return 'bg-warning text-dark';
    case 'error':
      return 'bg-danger';
    default:
      return 'bg-secondary';
  }
};

const TesterUsersTable: React.FC<TesterUsersTableProps> = ({
  title,
  users,
  usersTotal,
  loading,
  unreadByUserId,
  emptyMessage,
  onOpenChat,
  userProfileFrom,
  pagination,
  toolbar,
}) => {
  const showPhonePlatforms = userProfileFrom === 'internal-testers';
  const columnCount = showPhonePlatforms ? 8 : 9;

  return (
  <div className="mb-4">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h4 className="mb-0">{title}</h4>
      {!pagination && (
        <span className="text-muted small">{usersTotal.toLocaleString()} total</span>
      )}
    </div>

    {toolbar}

    {loading ? (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : (
      <div className="table-responsive">
        <table className="table table-striped table-sm align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              {showPhonePlatforms ? (
                <th>Platform</th>
              ) : (
                <>
                  <th>App</th>
                  <th>Beta Email</th>
                  <th>App Status</th>
                </>
              )}
              <th>Role</th>
              <th>Logging</th>
              <th>Last Login</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((u) => (
                <tr key={u._id}>
                  <td>{u.firstName} {u.lastName}</td>
                  <td className="text-truncate" style={{ maxWidth: 220 }} title={u.email}>
                    {u.email}
                  </td>
                  {showPhonePlatforms ? (
                    <td className="text-nowrap">
                      {(['ios', 'android'] as const).map((platform) => {
                        const hasPlatform = (u.phoneDevices || []).some((device) => (
                          device.platform?.toLowerCase() === platform
                        ));
                        const activeStyle = platform === 'android'
                          ? { backgroundColor: '#7c3aed', borderColor: '#7c3aed', color: '#fff' }
                          : { backgroundColor: '#0ea5e9', borderColor: '#0ea5e9', color: '#fff' };
                        const inactiveStyle = {
                          backgroundColor: '#f3f4f6',
                          color: '#d1d5db',
                          opacity: 0.42,
                        };
                        return (
                          <span
                            key={platform}
                            className={`badge rounded-pill me-1 ${hasPlatform ? '' : 'text-bg-light text-muted'}`}
                            style={hasPlatform ? activeStyle : inactiveStyle}
                            title={hasPlatform ? `${platform.toUpperCase()} phone added` : `No ${platform.toUpperCase()} phone`}
                          >
                            <span
                              aria-hidden="true"
                              className={`d-inline-block rounded-circle me-1 ${hasPlatform ? 'bg-white' : 'bg-secondary'}`}
                              style={{ width: 6, height: 6, opacity: hasPlatform ? 1 : 0.25 }}
                            />
                            {platform.toUpperCase()}
                          </span>
                        );
                      })}
                    </td>
                  ) : (
                    <>
                      <td className="text-nowrap">
                        {u.betaAppEnrollment?.platform ? (
                          <span className="badge text-bg-light text-uppercase">
                            {u.betaAppEnrollment.platform}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: 220 }}
                        title={u.betaAppEnrollment?.email || undefined}
                      >
                        {u.betaAppEnrollment?.email || <span className="text-muted">—</span>}
                      </td>
                      <td className="text-nowrap">
                        <span className={`badge ${enrollmentStatusBadgeClass(u.betaAppEnrollment?.status)}`}>
                          {enrollmentStatusLabel(u.betaAppEnrollment?.status)}
                        </span>
                      </td>
                    </>
                  )}
                  <td>
                    <span className={`badge ${roleBadgeClass(u.role)}`}>{u.role}</span>
                  </td>
                  <td>
                    <span
                      className="badge text-lowercase"
                      style={loggingModeBadgeStyle(u.loggingMode ?? DEFAULT_LOGGING_MODE)}
                    >
                      {loggingModeShortLabel(u.loggingMode ?? DEFAULT_LOGGING_MODE)}
                    </span>
                  </td>
                  <td className="text-nowrap text-muted small">
                    {u.lastLogin
                      ? new Date(u.lastLogin).toLocaleString()
                      : '—'}
                  </td>
                  <td className="text-end text-nowrap">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm me-2 position-relative"
                      onClick={() => onOpenChat(u)}
                      title="Open chat"
                    >
                      <FontAwesomeIcon icon={faComments} className="me-1" />
                      Chat
                      <UnreadBadge count={unreadByUserId[u._id] || 0} />
                    </button>
                    <SentryDropdown
                      issuesQuery={sentryExactQuery('user.id', u._id)}
                      logsFields={['timestamp', 'message', 'user.id']}
                    />
                    <Link
                      className="btn btn-outline-primary btn-sm"
                      to={`/users/${u._id}${userProfileFrom ? `?from=${userProfileFrom}` : ''}`}
                      title="View user"
                    >
                      View <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-1" />
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columnCount} className="text-center text-muted py-4">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )}

    {!loading && pagination && pagination.totalPages > 0 && (
      <div className="responsive-pagination d-flex justify-content-between align-items-center mt-2">
        <div className="fw-bold pt-1">
          {usersTotal.toLocaleString()} total users
        </div>
        <nav aria-label="User pagination">
          <ul className="pagination mb-0">
            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => pagination.onPageChange(1)}
                disabled={pagination.currentPage === 1}
                aria-label="First page"
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </button>
            </li>
            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
            </li>

            <li className="page-item disabled">
              <span className="page-link">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
            </li>

            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </li>
            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => pagination.onPageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
                aria-label="Last page"
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    )}
  </div>
  );
};

export default TesterUsersTable;
